import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Phone, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";
import { WHATSAPP_NUMBER, RESTAURANT_PHONE, RESTAURANT_PHONE_LABEL } from "../../lib/config";

const inputCls =
  "w-full bg-card2 border border-white/10 focus:border-gold/60 rounded-lg px-4 py-3.5 text-cream placeholder:text-cream-subtle/60 outline-none transition-colors duration-300 text-base";

const MONTHS_IT = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const WEEKDAYS_IT = ["L", "M", "M", "G", "V", "S", "D"];

export function romeToday() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

function isMondayStr(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).getDay() === 1;
}

export function isDateBookable(dateStr, today) {
  return !!dateStr && dateStr >= today && !isMondayStr(dateStr);
}

function formatDateIT(d) {
  if (!d) return "";
  const [y, m, day] = d.split("-");
  return y && m && day ? `${day}/${m}/${y}` : d;
}

function DatePicker({ value, onChange, today }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(value ? value.slice(0, 7) : today.slice(0, 7));
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    if (view < today.slice(0, 7)) setView(today.slice(0, 7));
  }, [today, view]);

  const [vy, vm] = view.split("-").map(Number);
  const cells = useMemo(() => {
    const first = new Date(vy, vm - 1, 1);
    const offset = (first.getDay() + 6) % 7;
    const days = new Date(vy, vm, 0).getDate();
    const arr = Array(offset).fill(null);
    for (let d = 1; d <= days; d++) arr.push(`${vy}-${String(vm).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    return arr;
  }, [vy, vm]);

  const shift = (dir) => {
    const d = new Date(vy, vm - 1 + dir, 1);
    const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (next >= today.slice(0, 7)) setView(next);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="booking-input-date"
        onClick={() => setOpen(!open)}
        className={`${inputCls} flex items-center justify-between gap-2 text-left ${value ? "" : "text-cream-subtle/60"}`}
      >
        <span>{value ? formatDateIT(value) : "Seleziona la data *"}</span>
        <CalendarDays className="w-4 h-4 text-gold-glow shrink-0" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            data-testid="booking-calendar"
            className="absolute left-0 right-0 sm:right-auto sm:w-[330px] top-full mt-2 z-40 bg-card2 border border-gold/25 rounded-xl p-4 shadow-2xl shadow-black/60"
          >
            <div className="flex items-center justify-between mb-3">
              <button type="button" onClick={() => shift(-1)} disabled={view <= today.slice(0, 7)} data-testid="cal-prev" aria-label="Mese precedente" className="p-2 rounded-lg text-cream-muted hover:text-gold-glow hover:bg-white/5 disabled:opacity-25 disabled:hover:bg-transparent transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <p className="font-serif text-cream text-base">{MONTHS_IT[vm - 1]} <span className="text-gold-glow">{vy}</span></p>
              <button type="button" onClick={() => shift(1)} data-testid="cal-next" aria-label="Mese successivo" className="p-2 rounded-lg text-cream-muted hover:text-gold-glow hover:bg-white/5 transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {WEEKDAYS_IT.map((w, i) => (
                <span key={i} className={`text-center text-[10px] font-bold uppercase tracking-wider py-1 ${i === 0 ? "text-burgundy-crimson/70" : "text-cream-subtle"}`}>{w}</span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((ds, i) => {
                if (!ds) return <span key={`x${i}`} />;
                const day = Number(ds.slice(8));
                const monday = isMondayStr(ds);
                const past = ds < today;
                const disabled = past || monday;
                const selected = ds === value;
                const isToday = ds === today;
                return (
                  <button
                    key={ds}
                    type="button"
                    disabled={disabled}
                    data-testid={`cal-day-${ds}`}
                    onClick={() => { onChange(ds); setOpen(false); }}
                    className={`h-10 rounded-lg text-sm transition-colors duration-200 ${
                      selected
                        ? "bg-burgundy-brand text-cream border border-gold/60 font-semibold"
                        : disabled
                          ? monday
                            ? "text-burgundy-crimson/40 bg-white/[0.03] cursor-not-allowed"
                            : "text-cream-subtle/25 cursor-not-allowed"
                          : "text-cream hover:bg-burgundy-brand/40"
                    } ${isToday && !selected ? "ring-1 ring-gold-glow/70" : ""}`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
              <p className="text-[11px] text-cream-subtle flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-sm bg-burgundy-crimson/40 inline-block" /> Lunedì: ristorante chiuso
              </p>
              <p className="text-[11px] text-cream-subtle/70 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-sm bg-white/10 inline-block" /> Date passate non selezionabili
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function buildWhatsAppMessage(form) {
  const lines = ["Ciao Red Stone! Vorrei prenotare un tavolo."];
  lines.push(`Nome: ${form.name.trim()}`);
  lines.push(`Cognome: ${form.surname.trim()}`);
  lines.push(`Data della prenotazione: ${formatDateIT(form.date)}`);
  lines.push(`Orario: ${form.time}`);
  lines.push(`Numero di persone: ${form.guests}`);
  if (form.phone.trim()) lines.push(`Telefono: ${form.phone.trim()}`);
  if (form.notes.trim()) lines.push(`Note e richieste speciali: ${form.notes.trim()}`);
  lines.push("Grazie!");
  return lines.join("\n");
}

export function BookingSection({ settings, prefill }) {
  const empty = { name: "", surname: "", phone: "", guests: 2, date: "", time: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [today, setToday] = useState(romeToday);

  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, ...prefill }));
  }, [prefill]);

  useEffect(() => {
    const tick = () => {
      const now = romeToday();
      if (now !== today) {
        setToday(now);
        setForm((f) => {
          if (f.date && !isDateBookable(f.date, now)) {
            toast.error("La data selezionata non è disponibile. Scegli un altro giorno da martedì a domenica.");
            return { ...f, date: "" };
          }
          return f;
        });
      }
    };
    const id = setInterval(tick, 30000);
    window.addEventListener("focus", tick);
    return () => { clearInterval(id); window.removeEventListener("focus", tick); };
  }, [today]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.surname.trim() || !form.time) {
      toast.error("Compila nome, cognome e orario prima di continuare.");
      return;
    }
    if (!isDateBookable(form.date, today)) {
      toast.error("La data selezionata non è disponibile. Scegli un altro giorno da martedì a domenica.");
      return;
    }
    const message = buildWhatsAppMessage(form);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
    toast.success("WhatsApp si sta aprendo con la tua prenotazione: premi invio per confermarla.");
  };

  return (
    <section id="prenota" className="relative py-24 sm:py-32 overflow-hidden" data-testid="booking-section">
      <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-burgundy-deep/25 to-obsidian pointer-events-none" />
      <div className="relative max-w-3xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center">
          <Eyebrow><span className="mx-auto">Prenotazioni</span></Eyebrow>
          <SectionTitle className="mt-4">
            Prenota il tuo <em className="italic text-gold-glow">tavolo</em>
          </SectionTitle>
          <p className="mt-4 text-sm sm:text-base text-cream-muted font-light">
            Compila il modulo: si apre WhatsApp con il messaggio già pronto, ti basta premere invio.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="booking-form-container"
            className="mt-10 bg-card2/70 backdrop-blur border border-white/10 rounded-2xl p-6 sm:p-9 grid sm:grid-cols-2 gap-4"
          >
            <input required data-testid="booking-input-name" value={form.name} onChange={set("name")} placeholder="Nome *" className={inputCls} />
            <input required data-testid="booking-input-surname" value={form.surname} onChange={set("surname")} placeholder="Cognome *" className={inputCls} />
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-cream-subtle mb-1.5">Numero di telefono (facoltativo)</label>
              <input type="tel" data-testid="booking-input-phone" value={form.phone} onChange={set("phone")} placeholder="Es. 333 1234567" className={inputCls} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-cream-subtle mb-1.5">Numero di persone *</label>
              <select data-testid="booking-input-guests" value={form.guests} onChange={set("guests")} className={inputCls}>
                {Array.from({ length: 19 }, (_, i) => i + 2).map((n) => (
                  <option key={n} value={n}>{n} persone</option>
                ))}
                <option value={21}>Più di 20 (gruppo)</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-cream-subtle mb-1.5">Data della prenotazione *</label>
              <DatePicker value={form.date} onChange={(d) => setForm((f) => ({ ...f, date: d }))} today={today} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-cream-subtle mb-1.5">Orario *</label>
              <input required type="time" data-testid="booking-input-time" value={form.time} onChange={set("time")} className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-cream-subtle mb-1.5">Note e richieste speciali (facoltativo)</label>
              <textarea
                data-testid="booking-input-notes"
                value={form.notes}
                onChange={set("notes")}
                placeholder="Compleanno, allergie, preferenza per un tavolo esterno o altre richieste…"
                rows={3}
                className={`${inputCls} resize-none`}
              />
            </div>
            <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3 mt-2">
              <button
                type="submit"
                data-testid="booking-whatsapp-submit"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-burgundy-brand hover:bg-burgundy-glow text-cream text-xs font-bold uppercase tracking-[0.22em] px-8 py-4 rounded-full border border-gold/30 hover:border-gold/60 transition-all duration-300"
              >
                <MessageCircle className="w-4 h-4" /> Prenota via WhatsApp
              </button>
              <a
                href={`tel:${RESTAURANT_PHONE}`}
                data-testid="booking-call-button"
                className="inline-flex items-center justify-center gap-2 border border-gold/40 hover:border-gold text-gold-glow text-xs font-bold uppercase tracking-[0.22em] px-8 py-4 rounded-full transition-colors"
              >
                <Phone className="w-4 h-4" /> Chiama Red Stone — {RESTAURANT_PHONE_LABEL}
              </a>
            </div>
            <p className="sm:col-span-2 text-[11px] text-cream-subtle leading-relaxed">
              Cliccando "Prenota via WhatsApp" si apre WhatsApp con il messaggio già compilato: controllalo e premi invio.
              Il tavolo è riservato solo dopo la nostra conferma. Siamo chiusi il lunedì.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
